import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Ticket, Company, User, Client, TicketComment } from '../api/supabaseEntities.js';
import { Button } from "../components/ui/button.jsx";
import { Plus, RefreshCw } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs.jsx";

import TicketKanban from '../components/tickets/TicketKanban.jsx';
import TicketList from '../components/tickets/TicketList.jsx';
import TicketFilters from '../components/tickets/TicketFilters.jsx';
import ExportButton from '../components/common/ExportButton.jsx';

export default function Tickets() {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [filters, setFilters] = useState(() => {
    const saved = localStorage.getItem('ticketFilters');
    return saved ? JSON.parse(saved) : {
      status: ['open', 'in_progress', 'analysis'],
      priority: 'all',
      type: 'all',
      company: 'all',
      client: 'all',
      assignee: 'all',
      searchTerm: '',
      dateRange: null
    };
  });
  const [viewMode, setViewMode] = useState('list'); // 'kanban' or 'list'

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      console.log('🔄 Carregando dados da página de tickets...');
      
      // Carrega o usuário atual
      const userData = User.getCurrentUser();
      console.log('👤 Usuário atual:', userData);
      setCurrentUser(userData);
      
      let ticketsData = [];
      let companiesData = [];
      let clientsData = [];
      let usersData = [];
      
      // Carrega dados baseados no perfil do usuário
      if (userData?.role === 'client') {
        console.log('👥 Carregando dados para cliente...');
        // Cliente vê apenas os chamados da sua empresa
        try {
          ticketsData = await Ticket.list({ company_id: userData.company_id });
          companiesData = userData.company_id ? await Company.list({ id: userData.company_id }) : [];
          clientsData = userData.company_id ? await Client.list({ company_id: userData.company_id }) : [];
        } catch (error) {
          console.error('❌ Erro ao carregar dados do cliente:', error);
          ticketsData = [];
          companiesData = [];
          clientsData = [];
        }
      } else {
        console.log('🏢 Carregando dados para usuário interno...');
        // Usuário interno vê todos os chamados, empresas, clientes e usuários
        try {
          const [ticketsResult, companiesResult, clientsResult, usersResult] = await Promise.allSettled([
            Ticket.list(),
            Company.list(),
            Client.list(),
            User.list()
          ]);
          
          ticketsData = ticketsResult.status === 'fulfilled' ? ticketsResult.value : [];
          companiesData = companiesResult.status === 'fulfilled' ? companiesResult.value : [];
          clientsData = clientsResult.status === 'fulfilled' ? clientsResult.value : [];
          usersData = usersResult.status === 'fulfilled' ? usersResult.value : [];
          
          console.log('📊 Dados carregados:', {
            tickets: ticketsData.length,
            companies: companiesData.length,
            clients: clientsData.length,
            users: usersData.length
          });
        } catch (error) {
          console.error('❌ Erro ao carregar dados internos:', error);
        }
      }
      
      setTickets(Array.isArray(ticketsData) ? ticketsData : []);
      setCompanies(Array.isArray(companiesData) ? companiesData : []);
      setClients(Array.isArray(clientsData) ? clientsData : []);
      setUsers(Array.isArray(usersData) ? usersData : []);
      
    } catch (error) {
      console.error('❌ Erro geral ao carregar dados:', error);
      setTickets([]);
      setCompanies([]);
      setClients([]);
      setUsers([]);
    }
    setIsLoading(false);
  };

  const handleTicketUpdate = async (ticketId, updates) => {
    try {
      console.log('✏️ Atualizando ticket:', { ticketId, updates });
      await Ticket.update(ticketId, updates);
      // Se for mudança de status para concluído/cancelado com motivo/solução, registrar atividade
      if ((updates.status === 'resolved' || updates.status === 'cancelled') && updates.reason && currentUser) {
        const STATUS_CONFIG = {
          open: { label: 'Em Aberto' },
          in_progress: { label: 'Em Andamento' },
          analysis: { label: 'Em Análise' },
          resolved: { label: 'Concluído' },
          cancelled: { label: 'Cancelado' },
          reopened: { label: 'Reaberto' }
        };
        const oldTicket = tickets.find(t => t.id === ticketId);
        const oldStatus = oldTicket?.status || 'open';
        const activityPayload = {
          ticket_id: ticketId,
          user_id: currentUser?.id,
          content: `Status alterado de "${STATUS_CONFIG[oldStatus]?.label || oldStatus}" para "${STATUS_CONFIG[updates.status]?.label || updates.status}"<br><b>Motivo/Solução:</b> ${updates.reason}`,
          is_internal: true,
          is_solution: false,
          attachments: [],
          time_spent: 0
        };
        await TicketComment.create(activityPayload);
      }
      await loadData(); // Recarrega os tickets após a atualização
      console.log('✅ Ticket atualizado com sucesso');
    } catch (error) {
      console.error('❌ Erro ao atualizar ticket:', error);
    }
  };

  const handleTicketDelete = async (ticketId) => {
    if (!confirm('Tem certeza que deseja excluir este chamado?')) return;
    
    try {
      console.log('🗑️ Excluindo ticket:', ticketId);
      await Ticket.delete(ticketId);
      await loadData(); // Recarrega os tickets após a exclusão
      console.log('✅ Ticket excluído com sucesso');
    } catch (error) {
      console.error('❌ Erro ao excluir ticket:', error);
    }
  };

  const handleTicketAssign = async (ticketId, statusOrUserId, reason) => {
    try {
      const ticket = tickets.find(t => t.id === ticketId);
      if (!ticket) return;
      // Se for status (resolved/cancelled) e motivo, atualizar status e criar atividade
      if ((statusOrUserId === 'resolved' || statusOrUserId === 'cancelled') && reason && currentUser) {
        await Ticket.update(ticketId, { ...ticket, status: statusOrUserId });
        const STATUS_CONFIG = {
          open: { label: 'Em Aberto' },
          in_progress: { label: 'Em Andamento' },
          analysis: { label: 'Em Análise' },
          resolved: { label: 'Concluído' },
          cancelled: { label: 'Cancelado' },
          reopened: { label: 'Reaberto' }
        };
        const activityPayload = {
          ticket_id: ticketId,
          user_id: currentUser?.id,
          content: `Status alterado de "${STATUS_CONFIG[ticket.status]?.label || ticket.status}" para "${STATUS_CONFIG[statusOrUserId]?.label || statusOrUserId}"<br><b>Motivo/Solução:</b> ${reason}`,
          is_internal: true,
          is_solution: false,
          attachments: [],
          time_spent: 0
        };
        await TicketComment.create(activityPayload);
        await loadData();
        return;
      }
      // Caso contrário, atribuição de responsável
      // Impedir atribuição de responsável em tickets concluídos
      if (['resolved', 'closed'].includes(ticket.status)) {
        console.log('❌ Não é possível alterar responsável de tickets concluídos');
        alert('Não é possível alterar o responsável de tickets concluídos.');
        return;
      }
      
      await Ticket.update(ticketId, { assigned_to: statusOrUserId });
      
      // Enviar webhook se um usuário foi atribuído e tem telefone
      if (statusOrUserId) {
        try {
          // Buscar dados do usuário atribuído
          const assigneeData = await User.findById(statusOrUserId);
          
          if (assigneeData?.phone) {
            console.log('📱 Enviando notificação via webhook para:', assigneeData.phone);
            
            const webhookPayload = {
              ticket_id: ticketId,
              ticket_title: ticket.title,
              ticket_description: ticket.description,
              assignee_name: assigneeData.name,
              assignee_email: assigneeData.email,
              assignee_phone: assigneeData.phone,
              assigned_by: currentUser?.name || 'Sistema',
              assigned_at: new Date().toISOString(),
              priority: ticket.priority,
              status: ticket.status
            };
            
            const response = await fetch('https://eoydy9430qifb0q.m.pipedream.net', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(webhookPayload)
            });
            
            if (response.ok) {
              console.log('✅ Webhook enviado com sucesso');
            } else {
              console.warn('⚠️ Falha ao enviar webhook:', response.status, response.statusText);
            }
          } else {
            console.log('📵 Usuário não possui telefone cadastrado, webhook não enviado');
          }
        } catch (webhookError) {
          console.error('❌ Erro ao enviar webhook:', webhookError);
          // Não interromper o fluxo principal por erro no webhook
        }
      }
      
      await loadData();
    } catch (error) {
      alert('Erro ao atualizar chamado: ' + (error.message || error));
    }
  };

  const filteredTickets = tickets.filter(ticket => {
    // Filtro de busca por título, descrição ou ID
    const matchSearch = !filters.searchTerm || 
      ticket.title?.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
      ticket.description?.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
      ticket.id?.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
      ticket.ticket_number?.toString().includes(filters.searchTerm);
    
    const matchStatus = filters.status === 'all' ? true : Array.isArray(filters.status) ? filters.status.includes(ticket.status) : ticket.status === filters.status;
    const matchPriority = filters.priority === 'all' ? true : Array.isArray(filters.priority) ? filters.priority.includes(ticket.priority) : ticket.priority === filters.priority;
    const matchType = filters.type === 'all' ? true : Array.isArray(filters.type) ? filters.type.includes(ticket.type) : ticket.type === filters.type;
    const matchCompany = filters.company === 'all' ? true : Array.isArray(filters.company) ? filters.company.includes(ticket.company_id) : ticket.company_id === filters.company;
    const matchClient = filters.client === 'all' ? true : Array.isArray(filters.client) ? filters.client.includes(ticket.client_id) : ticket.client_id === filters.client;
    const matchAssignee = filters.assignee === 'all' ? true : 
      Array.isArray(filters.assignee) ? 
        filters.assignee.some(assignee => {
          if (assignee === 'unassigned') return !ticket.assigned_to;
          if (assignee === 'me') return ticket.assigned_to === currentUser?.id;
          return ticket.assigned_to === assignee;
        }) :
        (filters.assignee === 'unassigned' && !ticket.assigned_to) || 
        (filters.assignee === 'me' && ticket.assigned_to === currentUser?.id) || 
        ticket.assigned_to === filters.assignee;
    
    return matchSearch && matchStatus && matchPriority && matchType && matchCompany && matchClient && matchAssignee;
  });

  if (!currentUser) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          <p className="text-sm text-gray-500">Carregando usuário...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold">Chamados</h1>
          <p className="text-muted-foreground mt-1">
            {currentUser.role === 'client' 
              ? 'Gerencie seus chamados' 
              : 'Gerencie todos os chamados do sistema'}
          </p>
          <div className="flex items-center gap-2 mt-2 text-sm text-gray-500">
            <span>{filteredTickets.length} chamados encontrados</span>
            {tickets.length !== filteredTickets.length && (
              <span>• {tickets.length} total</span>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <ExportButton
            data={filteredTickets}
            filename="tickets_export"
            title="Relatório de Chamados"
            type="tickets"
            variant="outline"
            size="sm"
            disabled={isLoading}
          />
          <Button
            variant="outline"
            size="sm"
            onClick={loadData}
            disabled={isLoading}
            className="flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
          <Tabs value={viewMode} onValueChange={setViewMode} className="hidden md:block">
            <TabsList>
              <TabsTrigger value="kanban">Kanban</TabsTrigger>
              <TabsTrigger value="list">Lista</TabsTrigger>
            </TabsList>
          </Tabs>
          <Button
            onClick={() => navigate('/NewTicket')}
            className="bg-primary"
          >
            <Plus className="w-4 h-4 mr-2" />
            Novo Chamado
          </Button>
        </div>
      </div>

      <TicketFilters 
        filters={filters} 
        setFilters={setFilters} 
        companies={companies}
        clients={clients}
        users={users}
        isInternalUser={currentUser.role !== 'client'}
      />

      {viewMode === 'kanban' ? (
        <TicketKanban
          tickets={filteredTickets}
          companies={companies}
          clients={clients}
          users={users}
          onUpdate={handleTicketUpdate}
          onDelete={handleTicketDelete}
          isLoading={isLoading}
          isInternalUser={currentUser.role !== 'client'}
        />
      ) : (
        <TicketList 
          tickets={filteredTickets} 
          companies={companies}
          clients={clients}
          users={users}
          onUpdate={handleTicketUpdate}
          onDelete={handleTicketDelete}
          onAssign={handleTicketAssign}
          isLoading={isLoading}
          isInternalUser={currentUser.role !== 'client'}
        />
      )}
    </div>
  );
}